// Pure logic for the Add Journey form: constants, validation, duration and the
// request payload. No React and no I/O so it is easy to unit-test. The backend
// validates everything again independently (CLAUDE.md Section 37) — these rules
// exist so people get an immediate, specific message, not as the safeguard.

import { placeToRouteItem } from "../../utils/tspPlace";
import { validatePlaceDate } from "./builder/journeyBuilderModel";

export const JOURNEY_NAME_MAX = 120;
export const JOURNEY_DESCRIPTION_MAX = 2000;
export const TEMPLE_NAME_MAX = 150;
export const LEADER_NAME_MAX = 120;

// Creating a journey starts it: the server saves it In Progress (no approval,
// no separate start). The status is the server's, never sent from this form.
//
// The Journey Builder asks only for the Team Leader and the places. The temple
// is never entered: it is the signed-in user's own, from their ICC account (the
// API's form options list it) — their primary temple when they have several.
// There is no journey name or description, and no Start / End Date: each place
// has its own visit date (cities[].plannedDate) and the journey runs from the
// first to the last of them (the server derives the same). The team leader is
// optional: an ICC user picked from the suggestions (leaderUserId set — the
// server stores that user), a name typed by hand (leaderUserId "" — plain text,
// not an account), or nobody.
export const EMPTY_JOURNEY_FORM = Object.freeze({
  templeName: "",
  leaderName: "",
  leaderUserId: "",
  cities: [],
});

// Top-to-bottom on screen — used to focus the first invalid field.
export const JOURNEY_FIELD_ORDER = ["templeName", "leader", "cities"];

export const NO_TEMPLE_MESSAGE = "Your account has no temple assigned. Ask an administrator to set your temple in the Admin Panel before creating a journey.";

// The temple must be one of the user's own (`temples`, from the API).
export function validateTempleName(value, temples = []) {
  if (temples.length === 0) return NO_TEMPLE_MESSAGE;
  const text = (value || "").trim();
  if (!text) return "Choose the temple this journey is for.";
  if (!temples.includes(text)) return "Choose one of the temples assigned to your account.";
  return null;
}

// Optional: only a typed name is checked (its length).
export function validateLeader({ leaderName, leaderUserId }) {
  if (leaderUserId) return null;
  const text = (leaderName || "").trim();
  if (text.length > LEADER_NAME_MAX) return `Team leader name must be ${LEADER_NAME_MAX} characters or fewer.`;
  return null;
}

// The leader as the API takes it: the picked user's id, else the typed name,
// else none (both null — on an edit that clears the leader).
export function leaderPayload({ leaderName, leaderUserId }) {
  if (leaderUserId) return { team_leader_user_id: Number(leaderUserId) };
  const name = (leaderName || "").trim();
  return name ? { team_leader_name: name } : { team_leader_user_id: null, team_leader_name: null };
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;


function isValidIsoDate(value) {
  if (!ISO_DATE.test(value)) return false;
  const parsed = new Date(`${value}T00:00:00Z`);
  return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
}

// Inclusive day count (start and end day both count). null when not computable.
export function countJourneyDays(startDate, endDate) {
  if (!isValidIsoDate(startDate) || !isValidIsoDate(endDate) || endDate < startDate) return null;
  const msPerDay = 24 * 60 * 60 * 1000;
  return Math.round((Date.parse(`${endDate}T00:00:00Z`) - Date.parse(`${startDate}T00:00:00Z`)) / msPerDay) + 1;
}

// Required: the temple (from the user's account), at least one place, and a
// valid visit date for every place. Optional: the team leader (checked only if
// typed). `errors.placeDates` = { [stopId]: message } for places without one.
export function validateJourney(values, { temples = [] } = {}) {
  const errors = {};

  const templeError = validateTempleName(values.templeName, temples);
  if (templeError) errors.templeName = templeError;
  const leaderError = validateLeader(values);
  if (leaderError) errors.leader = leaderError;

  if (values.cities.length === 0) errors.cities = "Add at least one place with its visit date.";
  const placeDates = {};
  values.cities.forEach((stop) => {
    const problem = validatePlaceDate(stop.plannedDate);
    if (problem) placeDates[stop.id] = problem;
  });
  if (Object.keys(placeDates).length) {
    errors.placeDates = placeDates;
    if (!errors.cities) errors.cities = "Every place needs its visit date.";
  }

  return errors;
}

// A route stop may carry OPTIONAL details — where it is and the areas planned
// there (e.g. a stop started from a point picked on the map). The API takes
// latitude + longitude as a pair and areas as a list; stops without them are
// sent exactly as before.
export const STOP_AREA_MAX = 100;
export const STOP_AREAS_MAX = 50;

export const stopHasPoint = (stop) => Number.isFinite(stop?.latitude) && Number.isFinite(stop?.longitude);

export function validateStopAreas(areas) {
  if ((areas || []).length > STOP_AREAS_MAX) return `A location can have at most ${STOP_AREAS_MAX} areas.`;
  const long = (areas || []).find((area) => area.length > STOP_AREA_MAX);
  return long ? `Each area must be ${STOP_AREA_MAX} characters or fewer.` : null;
}

export function stopToRouteItem(stop) {
  const item = placeToRouteItem(stop);
  if (stopHasPoint(stop)) Object.assign(item, { latitude: stop.latitude, longitude: stop.longitude });
  if (stop.areas && stop.areas.length) item.areas = [...stop.areas];
  if (stop.plannedDate) item.planned_date = stop.plannedDate;
  return item;
}

// Whitelist the fields sent onward (never spread form state into a request),
// under the API's own field names. No status: the server starts the journey.
// No start / end date: the server takes them from the places' planned_date.
// The temple is sent as the user's own (the server checks it against their
// account). An empty leader is left out.
export function buildJourneyPayload(values) {
  const leader = leaderPayload(values);
  return {
    temple_name: values.templeName.trim(),
    ...(leader.team_leader_user_id || leader.team_leader_name ? leader : {}),
    cities: values.cities.map(stopToRouteItem),
  };
}
